import React, { useEffect, useState } from 'react';
import {
  Box,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  IconButton,
  useColorModeValue,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  Button,
  VStack,
  HStack,
  Text,
  Tag,
  TagLabel,
  TagCloseButton,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import { FiSearch, FiFilter, FiX, FiSmile, FiStar, FiHeart, FiMeh, FiFrown } from 'react-icons/fi';
import { useSearch } from '../../../contexts/SearchContext';
import { useTags } from '../../../contexts/TagsContext';
import { useEncryption } from '../../../contexts/EncryptionContext';
import { debounce } from 'lodash';
import axios from 'axios';
import env from 'config/env';

interface Mood {
  label: string;
  value: number;
  icon: React.ReactElement;
  color: string;
}

const DEFAULT_MOODS: Mood[] = [
  { label: 'Amazing', value: 100, icon: <FiStar />, color: 'yellow.400' },
  { label: 'Happy', value: 80, icon: <FiSmile />, color: 'green.400' },
  { label: 'Good', value: 60, icon: <FiHeart />, color: 'blue.400' },
  { label: 'Okay', value: 40, icon: <FiMeh />, color: 'purple.400' },
  { label: 'Down', value: 20, icon: <FiFrown />, color: 'red.400' },
];

const SearchBar: React.FC = () => {
  const {
    searchQuery,
    setSearchQuery,
    filters,
    setFilters,
    performSearch,
    clearSearch,
  } = useSearch();
  const { tags } = useTags();
  const { decrypt } = useEncryption();
  const bgColor = useColorModeValue('white', 'gray.800');
  const [usedMoods, setUsedMoods] = useState<{ encrypted: string; decrypted: string }[]>([]);

  useEffect(() => {
    // Fetch used moods from the server
    const fetchUsedMoods = async () => {
      try {
        const response = await axios.get(`${env.API_URL}/moods`);
        const decryptedMoods = await Promise.all(
          response.data.map(async (encrypted: string) => ({
            encrypted,
            decrypted: await decrypt(encrypted)
          }))
        );
        setUsedMoods(decryptedMoods);
      } catch (error) {
        console.error('Error fetching used moods:', error);
      }
    };
    fetchUsedMoods();
  }, [decrypt]);

  const debouncedSearch = debounce(() => {
    performSearch();
  }, 300);

  useEffect(() => {
    if (searchQuery || Object.keys(filters).length > 0) {
      debouncedSearch();
    }
    return () => {
      debouncedSearch.cancel();
    };
  }, [searchQuery, filters, debouncedSearch]);

  const handleTagToggle = (tagId: string) => {
    const currentTags = filters.tags || [];
    const newTags = currentTags.includes(tagId)
      ? currentTags.filter((t) => t !== tagId)
      : [...currentTags, tagId];
    setFilters({ ...filters, tags: newTags });
  };

  const handleMoodToggle = (encrypted: string) => {
    const currentMoods = filters.moods || [];
    const newMoods = currentMoods.includes(encrypted)
      ? currentMoods.filter((m) => m !== encrypted)
      : [...currentMoods, encrypted];
    setFilters({ ...filters, moods: newMoods });
  };

  const handleClearFilters = () => {
    clearSearch();
  };

  const getMoodIcon = (label: string) => {
    const mood = DEFAULT_MOODS.find(m => m.label === label);
    return mood?.icon || <FiSmile />;
  };

  const getMoodColor = (label: string) => {
    const mood = DEFAULT_MOODS.find(m => m.label === label);
    return mood?.color || 'blue.400';
  };

  return (
    <Box mb={4}>
      <InputGroup>
        <InputLeftElement pointerEvents="none">
          <FiSearch />
        </InputLeftElement>
        <Input
          placeholder="Search journal entries..."
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            if (!e.target.value) {
              clearSearch();
            }
          }}
          bg={bgColor}
        />
        <InputRightElement width="auto" zIndex={2}>
          <HStack spacing={1}>
            {(searchQuery || Object.keys(filters).length > 0) && (
              <IconButton
                aria-label="Clear search"
                icon={<FiX />}
                size="sm"
                variant="ghost"
                onClick={handleClearFilters}
              />
            )}
            <Popover placement="bottom-end">
              <PopoverTrigger>
                <IconButton
                  aria-label="Filter"
                  icon={<FiFilter />}
                  size="sm"
                  variant="ghost"
                  colorScheme={Object.keys(filters).length > 0 ? 'blue' : 'gray'}
                />
              </PopoverTrigger>
              <PopoverContent width="300px">
                <PopoverBody p={4}>
                  <VStack spacing={4} align="stretch">
                    <Box>
                      <Text mb={2} fontWeight="medium">
                        Moods
                      </Text>
                      <Wrap spacing={2}>
                        {usedMoods.map((mood) => (
                          <WrapItem key={mood.encrypted}>
                            <Tag
                              size="sm"
                              variant={filters.moods?.includes(mood.encrypted) ? 'solid' : 'outline'}
                              cursor="pointer"
                              onClick={() => handleMoodToggle(mood.encrypted)}
                              colorScheme={getMoodColor(mood.decrypted).split('.')[0]}
                            >
                              <Box as="span" mr={1}>
                                {getMoodIcon(mood.decrypted)}
                              </Box>
                              <TagLabel>{mood.decrypted}</TagLabel>
                            </Tag>
                          </WrapItem>
                        ))}
                      </Wrap>
                    </Box>

                    <Box>
                      <Text mb={2} fontWeight="medium">
                        Tags
                      </Text>
                      <Box>
                        {tags.map((tag) => (
                          <Tag
                            key={tag.id}
                            size="sm"
                            variant={filters.tags?.includes(tag.id) ? 'solid' : 'outline'}
                            colorScheme="blue"
                            m={1}
                            cursor="pointer"
                            onClick={() => handleTagToggle(tag.id)}
                          >
                            <TagLabel>{tag.decrypted_name}</TagLabel>
                          </Tag>
                        ))}
                      </Box>
                    </Box>

                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={handleClearFilters}
                      isDisabled={Object.keys(filters).length === 0}
                    >
                      Clear Filters
                    </Button>
                  </VStack>
                </PopoverBody>
              </PopoverContent>
            </Popover>
          </HStack>
        </InputRightElement>
      </InputGroup>

      {/* Active Filters Display */}
      {Object.keys(filters).length > 0 && (
        <Box mt={2}>
          <HStack spacing={2} flexWrap="wrap">
            {filters.moods?.map((encrypted) => {
              const mood = usedMoods.find(m => m.encrypted === encrypted);
              return mood && (
                <Tag
                  key={encrypted}
                  size="sm"
                  variant="subtle"
                  colorScheme={getMoodColor(mood.decrypted).split('.')[0]}
                >
                  <Box as="span" mr={1}>
                    {getMoodIcon(mood.decrypted)}
                  </Box>
                  <TagLabel>{mood.decrypted}</TagLabel>
                  <TagCloseButton onClick={() => handleMoodToggle(encrypted)} />
                </Tag>
              );
            })}
            {filters.tags?.map((tagId) => {
              const tag = tags.find((t) => t.id === tagId);
              return (
                tag && (
                  <Tag
                    key={tagId}
                    size="sm"
                    variant="subtle"
                    colorScheme="blue"
                  >
                    <TagLabel>{tag.decrypted_name}</TagLabel>
                    <TagCloseButton onClick={() => handleTagToggle(tagId)} />
                  </Tag>
                )
              );
            })}
          </HStack>
        </Box>
      )}
    </Box>
  );
};

export default SearchBar; 